import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  useDroppable, rectIntersection, closestCenter,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import TodayIcon from '@mui/icons-material/Today';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import StarIcon from '@mui/icons-material/Star';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import ChecklistIcon from '@mui/icons-material/Checklist';
import CircularProgress from '@mui/material/CircularProgress';

import TaskCard from '@/components/workflow/taskmanager/TaskCard';
import TaskDetailModal from '@/components/workflow/taskmanager/TaskDetailModal';
import PersonalTodoList from '@/components/workflow/taskmanager/PersonalTodoList';
import MiniCalendar from '@/components/workflow/taskmanager/MiniCalendar';
import StaffLegend from '@/components/workflow/taskmanager/StaffLegend';
import ArchiveModal from '@/components/workflow/taskmanager/ArchiveModal';
import OverdueModal from '@/components/workflow/taskmanager/OverdueModal';

import {
  getTasks, getStaff, getOverdueCounts, getCalendar, moveTask, completeTask, bulkUpdateTasks,
} from '@/services/taskService';
import {
  toYMD, fromYMD, todayYMD, startOfWeek, addDays, weekdays, weekRangeLabel,
  dayLabel, dayNum, startOfMonth, endOfMonth, isWeekend,
} from '@/utils/taskDates';

const getCurrentManagerId = () => {
  try { return Number(jwtDecode(localStorage.getItem('jwtToken'))._id) || null; }
  catch { return null; }
};

const UNASSIGNED_COLOR = '#9CA3AF';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Compact "busy" chip for an event occupying a day column.
const DayEventChip = ({ event, onOpen }) => {
  const color = event.assignees?.length === 1 ? (event.assignees[0].color || UNASSIGNED_COLOR) : '#4F46E5';
  const names = (event.assignees || []).map((a) => a.name).join(', ') || 'No attendees';
  return (
    <button type="button" onClick={() => onOpen(event.id)} title={`${event.title} — ${names}`}
      style={{ backgroundColor: `${color}14`, borderColor: `${color}55`, borderLeft: `3px solid ${color}` }}
      className="w-full flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-left hover:shadow-sm transition">
      <EventOutlinedIcon style={{ fontSize: 13, color }} className="flex-shrink-0" />
      <span className="text-[11px] font-bold truncate flex-1" style={{ color }}>{event.title}</span>
      <span className="flex items-center -space-x-1 flex-shrink-0">
        {(event.assignees || []).slice(0, 3).map((a) => (
          <span key={a.id} title={a.name} className="w-3 h-3 rounded-full ring-2 ring-white"
            style={{ backgroundColor: a.color || UNASSIGNED_COLOR }} />
        ))}
        {(event.assignees || []).length > 3 && <span className="text-[9px] text-gray-400 pl-1">+{event.assignees.length - 3}</span>}
      </span>
    </button>
  );
};

// ── A single day column (used in Day + Week views) ────────────────────────────
const DayColumn = ({ date, tasks, events = [], isToday, onAddTask, onOpenEvent, onOpenTask, onComplete, onShiftDay, canShiftLeft, canShiftRight, selectable, selectedIds, onToggleSelect }) => {
  const ymd = toYMD(date);
  const { setNodeRef, isOver } = useDroppable({ id: ymd });
  const weekend = isWeekend(date);

  return (
    <div className="flex flex-col flex-1 min-w-[15rem] lg:min-w-0 min-h-0 rounded-2xl border border-gray-200 overflow-hidden bg-gray-50/60">
      <div className={`flex items-center justify-between px-3 py-2.5 flex-shrink-0 border-b
        ${isToday ? 'bg-[#002169] border-[#002169]' : weekend ? 'bg-gray-100 border-gray-200' : 'bg-white border-gray-100'}`}>
        <div className="flex items-baseline gap-1.5">
          <span className={`text-xs font-bold uppercase tracking-wide ${isToday ? 'text-amber-300' : 'text-gray-500'}`}>{dayLabel(date)}</span>
          <span className={`text-lg font-bold ${isToday ? 'text-white' : 'text-gray-700'}`}>{dayNum(date)}</span>
        </div>
        <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${isToday ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>{tasks.length}</span>
      </div>

      {events.length > 0 && (
        <div className="flex-shrink-0 p-2 pb-0 space-y-1.5">
          {events.map((e) => <DayEventChip key={e.id} event={e} onOpen={onOpenEvent} />)}
        </div>
      )}

      <div ref={setNodeRef}
        className={`flex-1 min-h-0 overflow-y-auto p-2 space-y-2 transition-colors ${isOver ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset' : ''}`}>
        <SortableContext items={tasks.map((t) => String(t.id))} strategy={verticalListSortingStrategy}>
          {tasks.map((t) => (
            <TaskCard key={t.id} task={t} overdue={t.due_date < todayYMD()}
              onOpen={() => onOpenTask(t.id)} onComplete={onComplete} onShiftDay={onShiftDay}
              canShiftLeft={canShiftLeft} canShiftRight={canShiftRight}
              selectable={selectable} selected={selectedIds?.has(t.id)} onToggleSelect={onToggleSelect} />
          ))}
        </SortableContext>
        {tasks.length === 0 && events.length === 0 && (
          <div className="flex items-center justify-center h-14 text-[11px] text-gray-300 italic">Nothing scheduled</div>
        )}
      </div>

      <button onClick={() => onAddTask(ymd)}
        className="flex-shrink-0 flex items-center justify-center gap-1 py-2 text-xs font-semibold text-gray-400 hover:text-[#002169] hover:bg-white border-t border-gray-100 transition">
        <AddIcon style={{ fontSize: 15 }} /> Add task
      </button>
    </div>
  );
};

// ── Month grid (read-only overview; click a day to drill into Day view) ───────
const MonthGrid = ({ monthDate, tasksByDay, eventsByDay, today, onPickDay, onOpenItem }) => {
  const first = startOfMonth(monthDate);
  const lead = (first.getDay() + 6) % 7;
  const total = endOfMonth(monthDate).getDate();
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(new Date(monthDate.getFullYear(), monthDate.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="flex-1 min-h-0 flex flex-col rounded-2xl border border-gray-200 overflow-hidden bg-white">
      <div className="grid grid-cols-7 flex-shrink-0 border-b border-gray-100 bg-gray-50">
        {DOW.map((d) => <div key={d} className="text-[11px] font-bold text-gray-400 text-center py-1.5">{d}</div>)}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto grid grid-cols-7 auto-rows-fr">
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="border-b border-r border-gray-50 bg-gray-50/40" />;
          const ymd = toYMD(d);
          const isToday = ymd === today;
          const evs = eventsByDay[ymd] || [];
          const tks = tasksByDay[ymd] || [];
          const items = [...evs.map((e) => ({ ...e, _ev: true })), ...tks];
          return (
            <div key={i} onClick={() => onPickDay(d)}
              className={`min-h-[78px] border-b border-r border-gray-100 p-1 cursor-pointer hover:bg-blue-50/40 transition flex flex-col gap-0.5 ${isWeekend(d) ? 'bg-gray-50/50' : ''}`}>
              <div className="flex justify-end">
                <span className={`text-[11px] font-bold w-5 h-5 flex items-center justify-center rounded-full ${isToday ? 'bg-[#002169] text-white' : 'text-gray-500'}`}>{d.getDate()}</span>
              </div>
              {items.slice(0, 3).map((it) => {
                const color = it._ev ? '#4F46E5' : (it.assignees?.[0]?.color || UNASSIGNED_COLOR);
                return (
                  <button key={`${it._ev ? 'e' : 't'}-${it.id}`} type="button"
                    onClick={(e) => { e.stopPropagation(); onOpenItem(it.id); }}
                    title={it.title}
                    style={{ backgroundColor: `${color}14`, color }}
                    className="w-full flex items-center gap-1 rounded px-1 py-0.5 text-left">
                    {it._ev && <EventOutlinedIcon style={{ fontSize: 9 }} className="flex-shrink-0" />}
                    {!it._ev && it.is_urgent && <StarIcon style={{ fontSize: 9, color: '#F59E0B' }} className="flex-shrink-0" />}
                    <span className="text-[9px] font-semibold truncate">{it.title}</span>
                  </button>
                );
              })}
              {items.length > 3 && <span className="text-[9px] text-gray-400 pl-1">+{items.length - 3} more</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TaskManager = () => {
  const managerId = useMemo(getCurrentManagerId, []);

  const [staff, setStaff]           = useState([]);
  const [tasks, setTasks]           = useState([]);
  const [events, setEvents]         = useState([]);
  const [overdue, setOverdue]       = useState({ total: 0, by_staff: [] });
  const [dayCounts, setDayCounts]   = useState({});
  const [loading, setLoading]       = useState(true);

  const [anchor, setAnchor]         = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [boardView, setBoardView]   = useState(
    () => localStorage.getItem('tmBoardView') || (window.innerWidth < 1024 ? 'day' : 'week')); // day|week|month
  const [calMonth, setCalMonth]     = useState(() => new Date());
  const [enabled, setEnabled]       = useState(new Set()); // staff ids whose cards are shown
  const [panelsOpen, setPanelsOpen] = useState(() => {
    const s = localStorage.getItem('tmPanels');
    return s != null ? s === '1' : window.innerWidth >= 1280;
  });

  const [modal, setModal]           = useState(null);
  const [showArchive, setShowArchive] = useState(false);
  const [showOverdue, setShowOverdue] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [activeId, setActiveId]     = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const isAdmin = useMemo(() => {
    const me = staff.find((s) => String(s.Id) === String(managerId));
    return !!(me && (me.IsPHAdmin === 1 || me.IsPHAdmin === true));
  }, [staff, managerId]);

  useEffect(() => { localStorage.setItem('tmBoardView', boardView); }, [boardView]);
  const togglePanels = () => setPanelsOpen((p) => { localStorage.setItem('tmPanels', p ? '0' : '1'); return !p; });

  // Team filter: which staff members' cards are shown. Toggled from the right sidebar.
  const toggleStaff = useCallback((id) => setEnabled((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  }), []);
  const allStaffSelected = staff.length > 0 && staff.every((s) => enabled.has(String(s.Id)));
  const toggleAllStaff = useCallback(() => {
    setEnabled(() => (staff.length > 0 && staff.every((s) => enabled.has(String(s.Id)))
      ? new Set()
      : new Set(staff.map((s) => String(s.Id)))));
  }, [staff, enabled]);

  // Deep link from Slack DMs: /task-manager?task=123 opens that task's card.
  useEffect(() => {
    const t = Number(new URLSearchParams(window.location.search).get('task'));
    if (Number.isFinite(t) && t > 0) {
      setModal({ mode: 'edit', taskId: t });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const weekStart = useMemo(() => startOfWeek(anchor), [anchor]);

  // Days rendered + data range for the active view.
  const displayedDays = useMemo(() => {
    if (boardView === 'day')  return [new Date(anchor)];
    if (boardView === 'week') return weekdays(weekStart);
    const start = startOfMonth(anchor), end = endOfMonth(anchor);
    const out = [];
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) out.push(new Date(d));
    return out;
  }, [boardView, anchor, weekStart]);

  const rangeFrom = useMemo(() => toYMD(displayedDays[0]), [displayedDays]);
  const rangeTo   = useMemo(() => toYMD(displayedDays[displayedDays.length - 1]), [displayedDays]);
  const today = todayYMD();

  // ── Loaders ──────────────────────────────────────────────────────────────
  const loadStaff = useCallback(async () => {
    try {
      const s = await getStaff() || [];
      setStaff(s);
      setEnabled(new Set(s.map((x) => String(x.Id))));
    } catch (e) { console.error(e); }
  }, []);

  const loadTasks = useCallback(async () => {
    try {
      const res = await getTasks({ from: rangeFrom, to: rangeTo });
      setTasks(res?.tasks || []);
      setEvents(res?.events || []);
    } catch (e) { console.error(e); }
  }, [rangeFrom, rangeTo]);

  const loadOverdue = useCallback(async () => {
    try { setOverdue(await getOverdueCounts() || { total: 0, by_staff: [] }); } catch (e) { console.error(e); }
  }, []);

  const loadCalendar = useCallback(async () => {
    try {
      const map = await getCalendar({
        from: toYMD(startOfMonth(calMonth)), to: toYMD(endOfMonth(calMonth)),
      });
      setDayCounts(map || {});
    } catch (e) { console.error(e); }
  }, [calMonth]);

  useEffect(() => { (async () => { setLoading(true); await loadStaff(); setLoading(false); })(); }, [loadStaff]);
  useEffect(() => { loadTasks(); }, [loadTasks]);
  useEffect(() => { loadOverdue(); }, [loadOverdue]);
  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  const refreshAll = useCallback(() => { loadTasks(); loadOverdue(); loadCalendar(); }, [loadTasks, loadOverdue, loadCalendar]);

  // ── Filtering ────────────────────────────────────────────────────────────
  const itemVisible = useCallback((item) => {
    const a = item.assignees || [];
    if (a.length === 0) return true; // unassigned items are always shown
    return a.some((p) => enabled.has(String(p.id)));
  }, [enabled]);

  const visibleTasks  = useMemo(() => tasks.filter(itemVisible), [tasks, itemVisible]);
  const visibleEvents = useMemo(() => events.filter(itemVisible), [events, itemVisible]);

  const dayKeys = useMemo(() => displayedDays.map(toYMD), [displayedDays]);

  const tasksByDay = useMemo(() => {
    const map = {};
    dayKeys.forEach((k) => { map[k] = []; });
    visibleTasks.forEach((t) => { if (map[t.due_date]) map[t.due_date].push(t); });
    return map;
  }, [visibleTasks, dayKeys]);

  const eventsByDay = useMemo(() => {
    const map = {};
    dayKeys.forEach((k) => { map[k] = []; });
    visibleEvents.forEach((e) => {
      const end = e.end_date || e.due_date;
      dayKeys.forEach((k) => { if (k >= e.due_date && k <= end) map[k].push(e); });
    });
    return map;
  }, [visibleEvents, dayKeys]);

  const weekCountsByStaff = useMemo(() => {
    const m = {};
    const bump = (item) => (item.assignees || []).forEach((p) => { m[String(p.id)] = (m[String(p.id)] || 0) + 1; });
    tasks.forEach(bump); events.forEach(bump);
    return m;
  }, [tasks, events]);

  const overdueByStaff = useMemo(() => {
    const m = {};
    (overdue.by_staff || []).forEach((r) => { if (r.assigned_to != null) m[String(r.assigned_to)] = r.count; });
    return m;
  }, [overdue]);

  // ── Actions ──────────────────────────────────────────────────────────────
  const handleComplete = async (task) => {
    setTasks((prev) => prev.filter((t) => t.id !== task.id));
    try { await completeTask(task.id); loadOverdue(); loadCalendar(); } catch { loadTasks(); }
  };

  const handleShiftDay = async (task, delta) => {
    const target = toYMD(addDays(fromYMD(task.due_date), delta));
    setTasks((prev) => prev.map((t) => t.id === task.id ? { ...t, due_date: target } : t));
    try { await moveTask(task.id, target); loadCalendar(); } catch { loadTasks(); }
  };

  const DAY_SET = useMemo(() => new Set(dayKeys), [dayKeys]);
  const collision = (args) => {
    const colHits = rectIntersection({ ...args, droppableContainers: args.droppableContainers.filter((c) => DAY_SET.has(String(c.id))) });
    return colHits.length > 0 ? colHits : closestCenter(args);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveId(null);
    if (!over) return;
    const activeStr = String(active.id), overStr = String(over.id);
    const task = tasks.find((t) => String(t.id) === activeStr);
    if (!task) return;
    let targetDay = DAY_SET.has(overStr) ? overStr : null;
    if (!targetDay) { const overTask = tasks.find((t) => String(t.id) === overStr); if (overTask) targetDay = overTask.due_date; }
    if (!targetDay || targetDay === task.due_date) return;
    setTasks((prev) => prev.map((t) => String(t.id) === activeStr ? { ...t, due_date: targetDay } : t));
    try { await moveTask(task.id, targetDay); loadCalendar(); } catch { loadTasks(); }
  };

  const draggedTask = activeId ? tasks.find((t) => String(t.id) === String(activeId)) : null;

  // ── Navigation ─────────────────────────────────────────────────────────────
  const step = (dir) => setAnchor((a) => {
    if (boardView === 'day')   return addDays(a, dir);
    if (boardView === 'week')  return addDays(a, dir * 7);
    return new Date(a.getFullYear(), a.getMonth() + dir, Math.min(a.getDate(), 28));
  });
  const goToday = () => setAnchor(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const pickDate = (date) => { const d = new Date(date); d.setHours(0, 0, 0, 0); setAnchor(d); };

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
  }, []);

  const selectAllVisible = useCallback(() => {
    setSelectedIds(new Set(tasks.map((t) => t.id)));
  }, [tasks]);

  const runBulk = useCallback(async (payload) => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      await bulkUpdateTasks({ ids: [...selectedIds], ...payload });
      exitSelectMode();
      refreshAll();
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Bulk update failed.');
    } finally {
      setBulkBusy(false);
    }
  }, [selectedIds, exitSelectMode, refreshAll]);

  const rangeLabel = boardView === 'day'
    ? `${dayLabel(anchor)} ${dayNum(anchor)} ${MONTHS[anchor.getMonth()].slice(0, 3)} ${anchor.getFullYear()}`
    : boardView === 'week'
      ? weekRangeLabel(weekStart)
      : `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;

  const defaultDate = today >= rangeFrom && today <= rangeTo ? today : rangeFrom;

  const ViewBtn = ({ v, label }) => (
    <button onClick={() => setBoardView(v)}
      className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition capitalize ${boardView === v ? 'bg-[#002169] text-white' : 'text-gray-500 hover:text-gray-700'}`}>
      {label}
    </button>
  );

  // ── Render ───────────────────────────────────────────────────────────────
  const renderBoard = () => {
    if (loading) return <div className="flex-1 flex items-center justify-center"><CircularProgress style={{ color: '#002169' }} /></div>;

    if (boardView === 'month') {
      return (
        <MonthGrid monthDate={anchor} tasksByDay={tasksByDay} eventsByDay={eventsByDay} today={today}
          onPickDay={(d) => { pickDate(d); setBoardView('day'); }}
          onOpenItem={(id) => setModal({ mode: 'edit', taskId: id })} />
      );
    }

    return (
      <DndContext sensors={sensors} collisionDetection={collision}
        onDragStart={({ active }) => setActiveId(active.id)} onDragEnd={handleDragEnd}>
        <div className={`flex gap-3 flex-1 min-h-0 overflow-x-auto lg:overflow-x-visible ${boardView === 'day' ? 'max-w-2xl mx-auto w-full' : ''}`}>
          {displayedDays.map((d, idx) => {
            const ymd = toYMD(d);
            return (
              <DayColumn key={ymd} date={d} tasks={tasksByDay[ymd] || []} events={eventsByDay[ymd] || []}
                selectable={selectMode} selectedIds={selectedIds} onToggleSelect={toggleSelect}
                isToday={ymd === today}
                onAddTask={(date) => setModal({ mode: 'create', itemType: 'task', defaultDate: date })}
                onOpenEvent={(id) => setModal({ mode: 'edit', taskId: id })}
                onOpenTask={(id) => setModal({ mode: 'edit', taskId: id })}
                onComplete={handleComplete} onShiftDay={handleShiftDay}
                canShiftLeft={boardView === 'day' || idx !== 0}
                canShiftRight={boardView === 'day' || idx !== displayedDays.length - 1} />
            );
          })}
        </div>
        <DragOverlay>
          {draggedTask && (
            <div className="bg-white rounded-xl shadow-xl border-2 border-blue-300 p-2.5 w-56 rotate-2">
              <p className="text-sm font-semibold text-gray-800 truncate">{draggedTask.title}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">{(draggedTask.assignees || []).map((a) => a.name).join(', ') || 'Unassigned'}</p>
            </div>
          )}
        </DragOverlay>
      </DndContext>
    );
  };

  return (
    <div className="flex gap-3 h-[calc(100vh-150px)]">
      {/* LEFT SIDEBAR */}
      {panelsOpen && (
        <aside className="hidden lg:flex flex-col w-64 flex-shrink-0 gap-3 overflow-hidden">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 flex-1 min-h-0 overflow-y-auto">
            <PersonalTodoList onPromoted={refreshAll} />
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 flex-shrink-0">
            <MiniCalendar viewMonth={calMonth} dayCounts={dayCounts} selectedWeekStart={weekStart}
              onPickDate={pickDate} onMonthChange={setCalMonth} />
          </div>
        </aside>
      )}

      {/* CENTRE — board */}
      <main className="flex-1 min-w-0 flex flex-col gap-3">
        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
          <button onClick={togglePanels} title={panelsOpen ? 'Hide side panels' : 'Show side panels'}
            className="hidden lg:flex items-center justify-center p-2 text-gray-500 bg-white border border-gray-100 shadow-sm rounded-xl hover:text-[#002169] hover:border-gray-300 transition">
            <ViewSidebarOutlinedIcon style={{ fontSize: 18 }} />
          </button>

          {/* Date nav */}
          <div className="flex items-center gap-1 bg-white rounded-xl border border-gray-100 shadow-sm px-1 py-1">
            <button onClick={() => step(-1)} className="p-1 text-gray-500 hover:text-[#002169] hover:bg-gray-50 rounded-lg transition"><ChevronLeftIcon /></button>
            <span className="text-sm font-bold text-[#002169] px-1.5 min-w-[140px] text-center">{rangeLabel}</span>
            <button onClick={() => step(1)} className="p-1 text-gray-500 hover:text-[#002169] hover:bg-gray-50 rounded-lg transition"><ChevronRightIcon /></button>
          </div>
          <button onClick={goToday}
            className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-100 shadow-sm rounded-xl hover:text-[#002169] hover:border-gray-300 transition">
            <TodayIcon style={{ fontSize: 16 }} /> Today
          </button>

          {/* Day / Week / Month */}
          <div className="flex bg-white rounded-xl border border-gray-100 shadow-sm p-1">
            <ViewBtn v="day" label="Day" />
            <ViewBtn v="week" label="Week" />
            <ViewBtn v="month" label="Month" />
          </div>

          <div className="flex-1" />

          {overdue.total > 0 && (
            <div className="flex items-stretch flex-shrink-0 whitespace-nowrap">
              <button
                onClick={() => {
                  if (overdue.earliest_due_date) {
                    pickDate(fromYMD(overdue.earliest_due_date));
                    setBoardView('day');
                  }
                }}
                title={overdue.earliest_due_date ? `Jump to earliest overdue: ${overdue.earliest_due_date}` : 'Overdue tasks'}
                className="flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded-l-lg border-r-0 px-2.5 py-2 hover:bg-red-100 hover:border-red-400 transition cursor-pointer">
                <WarningAmberIcon style={{ fontSize: 15 }} /> {overdue.total} overdue
              </button>
              <button
                onClick={() => setShowOverdue(true)}
                title="See all overdue tasks"
                className="flex items-center gap-1 text-xs font-bold text-red-700 bg-red-100 border border-red-200 rounded-r-lg px-2.5 py-2 hover:bg-red-200 hover:border-red-400 transition cursor-pointer">
                See all
              </button>
            </div>
          )}
          <button onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
            title={selectMode ? 'Exit selection' : 'Select multiple tasks'}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-xl border shadow-sm transition
              ${selectMode
                ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                : 'text-gray-600 bg-white border-gray-100 hover:border-gray-300 hover:text-gray-800'}`}>
            <ChecklistIcon style={{ fontSize: 16 }} /> <span className="hidden sm:inline">{selectMode ? 'Done' : 'Select'}</span>
          </button>
          <button onClick={() => setShowArchive(true)} title="Completed archive"
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-100 shadow-sm rounded-xl hover:border-gray-300 hover:text-gray-800 transition">
            <ArchiveOutlinedIcon style={{ fontSize: 16 }} /> <span className="hidden sm:inline">Archive</span>
          </button>
          {/* Events are created in the Team Calendar, not here — this board only
              creates tasks. Existing events still render and open for editing. */}
          <button onClick={() => setModal({ mode: 'create', itemType: 'task', defaultDate })}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#FFAF3C] text-[#002169] text-sm font-bold rounded-xl hover:bg-amber-400 shadow-sm transition">
            <AddIcon style={{ fontSize: 18 }} /> <span className="hidden sm:inline">New Task</span>
          </button>
        </div>

        {renderBoard()}
      </main>

      {/* RIGHT SIDEBAR — team legend */}
      {panelsOpen && (
        <aside className="hidden xl:block w-52 flex-shrink-0 overflow-y-auto">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3">
            <StaffLegend staff={staff} counts={weekCountsByStaff} overdue={overdueByStaff} enabled={enabled}
              isAdmin={isAdmin} allSelected={allStaffSelected} onToggle={toggleStaff} onToggleAll={toggleAllStaff}
              onColorChanged={() => { loadStaff(); loadTasks(); }} />
          </div>
        </aside>
      )}

      {/* MODALS */}
      {modal && (
        <TaskDetailModal mode={modal.mode} taskId={modal.taskId} defaultDate={modal.defaultDate}
          itemType={modal.itemType || 'task'} staff={staff} isAdmin={isAdmin}
          onClose={() => setModal(null)} onSaved={refreshAll} />
      )}
      {selectMode && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[1100] flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[#002169] text-white shadow-2xl">
          <span className="text-sm font-bold whitespace-nowrap">{selectedIds.size} selected</span>
          <button onClick={selectAllVisible} disabled={bulkBusy}
            className="text-xs font-semibold px-2 py-1 rounded-lg bg-white/15 hover:bg-white/25 transition disabled:opacity-50">
            Select all
          </button>
          <div className="w-px h-5 bg-white/25" />
          <button onClick={() => runBulk({ action: 'complete' })} disabled={bulkBusy || !selectedIds.size}
            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-green-500 hover:bg-green-600 transition disabled:opacity-40">
            Complete
          </button>
          <button onClick={() => { const d = prompt('Move to date (YYYY-MM-DD):', todayYMD()); if (d) runBulk({ action: 'move', due_date: d }); }}
            disabled={bulkBusy || !selectedIds.size}
            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 transition disabled:opacity-40">
            Move to…
          </button>
          <button onClick={() => runBulk({ action: 'urgent', is_urgent: true })} disabled={bulkBusy || !selectedIds.size}
            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 transition disabled:opacity-40">
            Mark urgent
          </button>
          <button onClick={() => runBulk({ action: 'archive' })} disabled={bulkBusy || !selectedIds.size}
            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 transition disabled:opacity-40">
            Archive
          </button>
          {isAdmin && (
            <button
              onClick={() => { if (window.confirm(`Delete ${selectedIds.size} task(s)? This cannot be undone.`)) runBulk({ action: 'delete' }); }}
              disabled={bulkBusy || !selectedIds.size}
              className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 transition disabled:opacity-40">
              Delete
            </button>
          )}
          <div className="w-px h-5 bg-white/25" />
          <button onClick={exitSelectMode} disabled={bulkBusy}
            className="text-xs font-semibold px-2 py-1 rounded-lg hover:bg-white/15 transition">
            Cancel
          </button>
        </div>
      )}

      {showArchive && <ArchiveModal staff={staff} onClose={() => setShowArchive(false)} onChanged={refreshAll} />}
      {showOverdue && (
        <OverdueModal
          onClose={() => setShowOverdue(false)}
          onJump={(ymd) => { pickDate(fromYMD(ymd)); setBoardView('day'); setShowOverdue(false); }}
          onOpenTask={(id) => { setShowOverdue(false); setModal({ mode: 'edit', taskId: id }); }}
        />
      )}
    </div>
  );
};

export default TaskManager;
